import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  RemovePlayerDepositLimitMutation,
  RemovePlayerDepositLimitMutationVariables,
} from './__generated__/PlayerRemoveDepositLimitForm';

const query = graphql`
  query SanityPlayerRemoveDepositLimitForm {
    sanityPlayerRemoveDepositLimitsForm {
      title {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const removeDepositLimitMutation = gql`
  mutation RemovePlayerDepositLimit(
    $playerId: ID!
    $limitPeriodType: LimitTypeEnum!
  ) {
    removeDepositLimit(playerId: $playerId, limitPeriodType: $limitPeriodType) {
      id
      depositLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerRemoveDepositLimitForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
}> = ({ playerId, limitPeriodType }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerRemoveDepositLimitFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerRemoveDepositLimitsForm;

  const [removePlayerDepositLimitState, removePlayerDepositLimit] = useMutation<
    RemovePlayerDepositLimitMutation,
    RemovePlayerDepositLimitMutationVariables
  >(removeDepositLimitMutation);

  const defaultValues = {};

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (_values: typeof defaultValues) => {
    if (playerId) {
      removePlayerDepositLimit({
        playerId,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
        } else if (close) {
          close();
        }
      });
    }
  };

  if (!form || !defaultValues) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={removePlayerDepositLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerRemoveDepositLimitForm;
